import { useEffect, useRef, useState, type ComponentProps, type KeyboardEvent, type ReactElement } from 'react';
import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react';
import { CampoSelect } from './campo-select';
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from './popover';
import { calendarDateAllowed, calendarIso, displayCalendarDate, parseCalendarDate } from '../../lib/calendar-date';
import { cn } from '../../lib/utils';
import '../../styles/calendar.css';

type DatePickerProps = Omit<ComponentProps<'input'>, 'type' | 'onChange' | 'value' | 'defaultValue' | 'ref' | 'min' | 'max'> & {
  readonly value: string;
  readonly onValueChange: (value: string) => void;
  readonly min?: string;
  readonly max?: string;
};

const MONTHS = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho', 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
const WEEKDAYS = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

export function DatePicker({ value, onValueChange, className, min, max, disabled, readOnly, name, ...props }: DatePickerProps): ReactElement {
  const today = calendarIso(new Date());
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(() => displayCalendarDate(value));
  const emitted = useRef(value);
  const [view, setView] = useState(() => new Date(`${parseCalendarDate(value) || today}T12:00:00`));
  const [focusDate, setFocusDate] = useState(value || today);
  const focusRequested = useRef(false);
  const daysRef = useRef(new Map<string, HTMLButtonElement>());
  useEffect(() => {
    // Mantém o texto parcial digitado; mudanças externas (limpar filtros) sincronizam o campo.
    if (value !== emitted.current) { emitted.current = value; setDraft(displayCalendarDate(value)); }
  }, [value]);
  useEffect(() => {
    if (open && focusRequested.current) { daysRef.current.get(focusDate)?.focus(); focusRequested.current = false; }
  }, [open, view, focusDate]);

  function change(iso: string): void {
    emitted.current = iso;
    onValueChange(iso);
  }
  function choose(iso: string): void {
    change(iso); setDraft(displayCalendarDate(iso)); setOpen(false);
  }
  function openCalendar(next: boolean): void {
    if (next) {
      const initial = parseCalendarDate(value) || (min && today < min ? min : max && today > max ? max : today);
      setView(new Date(`${initial}T12:00:00`)); setFocusDate(initial);
    }
    setOpen(next);
  }
  function dayKey(event: KeyboardEvent<HTMLButtonElement>, iso: string): void {
    const date = new Date(`${iso}T12:00:00`);
    const offsets: Record<string, number> = { ArrowLeft: -1, ArrowRight: 1, ArrowUp: -7, ArrowDown: 7, Home: -date.getDay(), End: 6 - date.getDay() };
    const offset = offsets[event.key];
    if (offset === undefined && event.key !== 'PageUp' && event.key !== 'PageDown') return;
    event.preventDefault();
    if (offset !== undefined) date.setDate(date.getDate() + offset);
    else { const day = date.getDate(); date.setDate(1); date.setMonth(date.getMonth() + (event.key === 'PageUp' ? -1 : 1)); date.setDate(Math.min(day, new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate())); }
    const next = calendarIso(date);
    if (!calendarDateAllowed(next, min, max)) return;
    focusRequested.current = true; setFocusDate(next); setView(date);
  }
  const first = new Date(view.getFullYear(), view.getMonth(), 1, 12);
  const gridStart = new Date(first); gridStart.setDate(1 - first.getDay());
  const days = Array.from({ length: 42 }, (_, i) => { const date = new Date(gridStart); date.setDate(date.getDate() + i); return date; });
  const minYear = min ? new Date(`${min}T12:00:00`).getFullYear() : Math.min(1900, view.getFullYear());
  const maxYear = max ? new Date(`${max}T12:00:00`).getFullYear() : Math.max(new Date().getFullYear() + 30, view.getFullYear());
  function setMonth(date: Date): void { setView(date); setFocusDate(calendarIso(date)); }
  function shiftMonth(delta: number): void { setMonth(new Date(view.getFullYear(), view.getMonth() + delta, 1, 12)); }
  const invalidDraft = Boolean(draft) && !calendarDateAllowed(parseCalendarDate(draft), min, max);

  return <Popover open={open} onOpenChange={openCalendar}>
    <PopoverAnchor asChild><div className="date-picker-field">
      <input {...props} type="text" inputMode="numeric" placeholder="dd/mm/aaaa" value={draft} disabled={disabled} readOnly={readOnly}
        data-slot="input" className={cn('date-picker-input', className)} aria-invalid={invalidDraft || props['aria-invalid']}
        onChange={(event) => { const text = event.target.value; setDraft(text); const iso = parseCalendarDate(text); change(calendarDateAllowed(iso, min, max) ? iso : ''); }}
        onKeyDown={(event) => { if (!disabled && !readOnly && event.key === 'ArrowDown' && event.altKey) { event.preventDefault(); openCalendar(true); } props.onKeyDown?.(event); }} />
      {name && <input type="hidden" name={name} value={value} />}
      <PopoverTrigger asChild><button type="button" disabled={disabled || readOnly} className="date-picker-trigger" aria-label={`Selecionar data${draft ? `: ${draft}` : ''}`}>
        <CalendarDays size={17} aria-hidden="true" />
      </button></PopoverTrigger>
    </div></PopoverAnchor>
    <PopoverContent className="platform-calendar" aria-label="Escolher data" onOpenAutoFocus={(event) => { event.preventDefault(); daysRef.current.get(focusDate)?.focus(); }}>
      <div className="platform-calendar__heading">
        <button type="button" aria-label="Mês anterior" onClick={() => shiftMonth(-1)} disabled={Boolean(min && calendarIso(new Date(view.getFullYear(), view.getMonth(), 0, 12)) < min)}><ChevronLeft size={17} /></button>
        <CampoSelect size="sm" aria-label="Mês" value={String(view.getMonth())} onValueChange={(valor) => setMonth(new Date(view.getFullYear(), Number(valor), 1, 12))}
          options={MONTHS.map((month, i) => ({ value: String(i), label: month }))} className="platform-calendar__month" />
        <CampoSelect size="sm" aria-label="Ano" value={String(view.getFullYear())} onValueChange={(valor) => setMonth(new Date(Number(valor), view.getMonth(), 1, 12))}
          options={Array.from({ length: maxYear - minYear + 1 }, (_, i) => String(minYear + i)).map((year) => ({ value: year, label: year }))} className="platform-calendar__year" />
        <button type="button" aria-label="Próximo mês" onClick={() => shiftMonth(1)} disabled={Boolean(max && calendarIso(new Date(view.getFullYear(), view.getMonth() + 1, 1, 12)) > max)}><ChevronRight size={17} /></button>
      </div>
      <div role="grid" aria-label={`${MONTHS[view.getMonth()]} de ${String(view.getFullYear())}`}>
        <div role="row" className="platform-calendar__weekdays">{WEEKDAYS.map(day => <span role="columnheader" key={day}>{day}</span>)}</div>
        {Array.from({ length: 6 }, (_, week) => <div role="row" className="platform-calendar__week" key={week}>
          {days.slice(week * 7, week * 7 + 7).map(date => { const iso = calendarIso(date); return <div role="gridcell" aria-selected={iso === value} key={iso}>
            <button type="button" ref={el => { if (el) daysRef.current.set(iso, el); else daysRef.current.delete(iso); }}
              aria-label={date.toLocaleDateString('pt-BR', { day: 'numeric', month: 'long', year: 'numeric' })} aria-current={iso === today ? 'date' : undefined}
              data-selected={iso === value} data-outside={date.getMonth() !== view.getMonth()} disabled={!calendarDateAllowed(iso, min, max)}
              tabIndex={iso === focusDate ? 0 : -1} onKeyDown={event => dayKey(event, iso)} onClick={() => choose(iso)}>{date.getDate()}</button>
          </div>; })}
        </div>)}
      </div>
      <div className="platform-calendar__footer">
        <button type="button" onClick={() => choose('')} disabled={props.required}>Limpar</button>
        <button type="button" onClick={() => choose(today)} disabled={!calendarDateAllowed(today, min, max)}>Hoje</button>
      </div>
      <p className="sr-only">Use as setas para navegar pelos dias, Enter para selecionar e Escape para fechar.</p>
    </PopoverContent>
  </Popover>;
}
